import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiMail, FiLock, FiArrowRight } from "react-icons/fi";
import { ApiHandler } from "../../helper/ApiHandler";
import { setUserProfileData, setUserToken } from "../../redux/slices/userSlice";
import illustration from "../../assest/Secure-login-bro.png"
import SEO from "../../utils/Seo";

const LOGIN_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,500;0,9..144,600;1,9..144,500&family=Manrope:wght@400;500;600;700;800&display=swap');

  .lg-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .lg-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }

  @keyframes lg-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
  .lg-float { animation: lg-float 4.5s ease-in-out infinite; }

  @media (prefers-reduced-motion: reduce) {
    .lg-float { animation: none !important; }
  }
`;

const Login = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    const body = {
      email: formData.email,
      password: formData.password,
    };

    try {
      const result = await ApiHandler(
        "/login.php",
        "POST",
        body,
        undefined,
        dispatch,
        navigate
      );

      if (result.data.status === "1") {
        const getProfile = await ApiHandler(
          "/get_profile.php",
          "GET",
          undefined,
          result.data.data.token,
          dispatch,
          navigate
        );
        if (getProfile.data.status === "1") {
          toast.success(result.data.msg);
          dispatch(setUserToken(result.data.data.token));
          dispatch(setUserProfileData(getProfile.data.data[0]));
          navigate("/dashboard");
        }
      } else {
        toast.error(result.data.msg);
        setFormData({
          ...formData,
          email: "",
          password: "",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Login - MyAd360 Publisher Panel"
        description="Login to MyAd360 dashboard to manage your ad monetization, view reports, and optimize your ad revenue."
      />
      <style>{LOGIN_STYLES}</style>

      <div
        className="lg-body min-h-screen flex items-center justify-center px-4 py-8"
        style={{
          background:
            "radial-gradient(90% 70% at 15% 0%, #FFF4E5 0%, transparent 55%), radial-gradient(80% 60% at 100% 100%, #FEF3E5 0%, transparent 55%), #FFFBF5",
        }}
      >
        <div
          className="max-w-6xl w-full bg-white shadow-[0_30px_80px_-25px_rgba(28,20,16,0.25)] overflow-hidden grid grid-cols-1 md:grid-cols-2 border border-[#F0DCC8]"
          style={{ borderRadius: "2.5rem 1rem 2.5rem 1rem" }}
        >
          {/* Left: Illustration */}
          <div
            className="hidden md:flex items-center justify-center p-6 relative overflow-hidden"
            style={{
              background:
                "radial-gradient(120% 100% at 30% 20%, #FEF3E5 0%, #FFF4E5 60%, #FFFBF5 100%)",
            }}
          >
            <div
              aria-hidden="true"
              className="absolute -top-10 -left-10 w-56 h-56 rounded-full opacity-30 blur-3xl"
              style={{ background: "radial-gradient(circle, #F59E0B, transparent 70%)" }}
            />
            <div
              aria-hidden="true"
              className="absolute -bottom-14 -right-10 w-56 h-56 rounded-full opacity-25 blur-3xl"
              style={{ background: "radial-gradient(circle, #D97706, transparent 70%)" }}
            />
            <img
  src={illustration}
  alt="Login Illustration"
  className="lg-float max-h-[420px] w-auto relative z-10"
/>
          </div>

          {/* Right: Login Form */}
          <div className="p-8 sm:p-12">
            <h2
              className="text-3xl font-bold text-center text-[#1C1410]"
              style={{ fontFamily: "'Fraunces', ui-serif, Georgia, serif" }}
            >
              Welcome{" "}
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#D97706] via-[#E8882D] to-[#EA580C]">
                Back
              </span>
            </h2>
            <p className="text-center text-[#8A7560] mb-6 text-sm">Login to your account</p>

            <form onSubmit={handleSubmit}>
              <div className="mb-5">
                <label htmlFor="email" className="block mb-1 text-sm font-medium text-[#3D2E22]">
                  Email
                </label>
                <div className="relative">
                  <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A08E7A]" />
                  <input
  type="email"
  id="email"
  name="email"
  value={formData.email}
  onChange={handleChange}
  required
  placeholder="you@example.com"
  className="w-full h-12 pl-11 pr-4 bg-white border border-[#F0DCC8] rounded-xl text-sm focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-all"
/>
                </div>
              </div>
              <div className="mb-4">
                <label htmlFor="password" className="block mb-1 text-sm font-medium text-[#3D2E22]">
                  Password
                </label>
                <div className="relative">
                  <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-[#A08E7A]" />
                  <input
  type="password"
  id="password"
  name="password"
  value={formData.password}
  onChange={handleChange}
  required
  placeholder="Enter your password"
  className="w-full h-12 pl-11 pr-4 bg-white border border-[#F0DCC8] rounded-xl text-sm focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-all"
/>
                </div>
              </div>

              <div className="text-right mb-5">
                <span
                  className="text-sm text-[#D97706] font-semibold cursor-pointer hover:text-[#B45309] hover:underline transition-colors"
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot Password?
                </span>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="group w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D97706] to-[#B45309] hover:from-[#7C3A0E] hover:to-[#D97706] text-white py-3 rounded-xl text-sm font-bold shadow-[0_16px_34px_-14px_rgba(217,119,6,0.5)] transition-all duration-300 disabled:opacity-70"
              >
                {isSubmitting ? "Logging in..." : "Log In"}
                {!isSubmitting && (
                  <FiArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                )}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-[#8A7560]">
              Don't have an account?{" "}
              <span
                className="text-[#D97706] font-bold cursor-pointer hover:text-[#B45309] hover:underline transition-colors"
                onClick={() => navigate("/signup")}
              >
                Sign Up
              </span>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;